/**
 * batch-66 kluis-niveaus (FO-495) - beveiligingsniveaus, toegangslijst en
 * eigenaar per Kluis-geheim.
 *
 * De gedeelde bron voor API, backoffice en portal: de niveaus met hun rangorde,
 * de onthulvereisten per niveau, de subject-/rechtsoorten van de toegangslijst,
 * de Zod-schema's van de mutaties (API valideert ermee, het formulier vooraf) en
 * de DTO's. Alles hier is puur: geen DB, geen IO.
 *
 * Twee regels die nergens anders mogen staan:
 *
 *  1. **De rangorde is de enige definitie van "strenger".** `STANDAARD <
 *     BEPERKT < VERTROUWELIJK < STRIKT`. Het categorieminimum, de keuzelijst in
 *     het formulier en de 422 bij verlagen lezen allemaal `kluisNiveauVoldoet`.
 *  2. **De rol ACCOUNTANT is nooit een subject.** De toegangslijst kent alleen
 *     `KLUIS_TOEGANG_ROLLEN`; ADMIN staat daar bewust ook niet in (die loopt via
 *     het niveau zelf, niet via een lijstregel).
 */
import { z } from 'zod'

// ─── Niveaus ────────────────────────────────────────────────────────────────

export const KLUIS_NIVEAUS = ['STANDAARD', 'BEPERKT', 'VERTROUWELIJK', 'STRIKT'] as const
export type KluisNiveau = (typeof KLUIS_NIVEAUS)[number]

export const KLUIS_NIVEAU_LABEL: Record<KluisNiveau, string> = {
  STANDAARD: 'Standaard',
  BEPERKT: 'Beperkt',
  VERTROUWELIJK: 'Vertrouwelijk',
  STRIKT: 'Strikt',
}

/** Eén zin per niveau: wie erbij mag en wat onthullen vraagt. */
export const KLUIS_NIVEAU_UITLEG: Record<KluisNiveau, string> = {
  STANDAARD: 'Alleen de eigenaar, beheerders en wie met naam toegang heeft.',
  BEPERKT: 'Alleen de eigenaar, beheerders en wie met naam toegang heeft.',
  VERTROUWELIJK:
    'Alleen de eigenaar, beheerders en wie met naam toegang heeft. Onthullen vraagt een reden.',
  STRIKT:
    'Alleen de eigenaar en met naam genoemde personen. Onthullen vraagt een reden en een bevestigingscode; een beheerder heeft een 4-ogen-vrijgave nodig.',
}

/** Rangorde: hoger = strenger. */
export function kluisNiveauRang(niveau: KluisNiveau): number {
  return KLUIS_NIVEAUS.indexOf(niveau)
}

/** Is `niveau` minstens zo streng als `minimum`? */
export function kluisNiveauVoldoet(niveau: KluisNiveau, minimum: KluisNiveau): boolean {
  return kluisNiveauRang(niveau) >= kluisNiveauRang(minimum)
}

/** De niveaus die een geheim in een categorie met dit minimum mag hebben. */
export function kluisNiveausVanaf(minimum: KluisNiveau): KluisNiveau[] {
  return KLUIS_NIVEAUS.filter((n) => kluisNiveauVoldoet(n, minimum))
}

/**
 * Telt de toegangslijst op dit niveau? Sinds spec 224 (toegang per persoon) op
 * ELK niveau: een geheim is alleen zichtbaar voor beheerders, de eigenaar en wie
 * er met naam op de lijst staat. Er zijn geen categorieregels meer die een
 * geheim op niveau Standaard voor iedereen openen.
 */
export function kluisNiveauKentLijst(_niveau: KluisNiveau): boolean {
  return true
}

/**
 * Mogen op dit niveau alleen personen op de lijst? Sinds spec 224 op elk niveau:
 * toegang geef en ontzeg je per persoon, niet per rol of afdeling.
 */
export function kluisNiveauAlleenPersonen(_niveau: KluisNiveau): boolean {
  return true
}

// ─── Onthullen per niveau ───────────────────────────────────────────────────

export interface KluisOnthulVereisten {
  /** Een reden is verplicht en landt in de audit. */
  reden: boolean
  /** Herbevestiging (stap-up) is verplicht vóór de waarde vrijkomt. */
  herbevestiging: boolean
  /** De eigenaar krijgt bij elke onthulling een melding. */
  eigenaarMelding: boolean
  /** Hoe lang de waarde zichtbaar blijft voor hij weer wordt verborgen. */
  zichtbaarSeconden: number
}

/**
 * STANDAARD en BEPERKT onthullen zoals vóór FO-495 (30 seconden, geen reden).
 * VERTROUWELIJK: reden verplicht, 60 seconden (spec §2.B.4). STRIKT: reden plus
 * herbevestiging plus een melding aan de eigenaar.
 */
export const KLUIS_ONTHUL_VEREISTEN: Record<KluisNiveau, KluisOnthulVereisten> = {
  STANDAARD: { reden: false, herbevestiging: false, eigenaarMelding: false, zichtbaarSeconden: 30 },
  BEPERKT: { reden: false, herbevestiging: false, eigenaarMelding: false, zichtbaarSeconden: 30 },
  VERTROUWELIJK: { reden: true, herbevestiging: false, eigenaarMelding: false, zichtbaarSeconden: 60 },
  STRIKT: { reden: true, herbevestiging: true, eigenaarMelding: true, zichtbaarSeconden: 60 },
}

/** Minimale lengte van een onthul-reden: een losse letter is geen reden. */
export const KLUIS_REDEN_MIN_LENGTE = 5
export const KLUIS_REDEN_MAX_LENGTE = 500

// ─── Toegangslijst ──────────────────────────────────────────────────────────

export const KLUIS_TOEGANG_SUBJECT_TYPES = ['ROL', 'MEDEWERKER', 'AFDELING'] as const
export type KluisToegangSubjectType = (typeof KLUIS_TOEGANG_SUBJECT_TYPES)[number]

export const KLUIS_TOEGANG_SUBJECT_LABEL: Record<KluisToegangSubjectType, string> = {
  ROL: 'Rol',
  MEDEWERKER: 'Persoon',
  AFDELING: 'Afdeling',
}

export const KLUIS_TOEGANG_RECHTEN = ['ZIEN_BESTAAN', 'ONTHULLEN', 'BEWERKEN'] as const
export type KluisToegangRecht = (typeof KLUIS_TOEGANG_RECHTEN)[number]

export const KLUIS_TOEGANG_RECHT_LABEL: Record<KluisToegangRecht, string> = {
  ZIEN_BESTAAN: 'Bestaan zien',
  ONTHULLEN: 'Onthullen',
  BEWERKEN: 'Bewerken',
}

/**
 * Rollen die als subject op een toegangslijst mogen. ACCOUNTANT nooit (FO-404);
 * ADMIN niet omdat die via het niveau loopt en een lijstregel daar niets aan
 * toevoegt (en bij STRIKT juist niets mag toevoegen).
 */
export const KLUIS_TOEGANG_ROLLEN = [
  'MONTEUR',
  'TEAMLEIDER',
  'PLANNER',
  'HR',
  'SALARIS',
  'MANAGER',
] as const
export type KluisToegangRol = (typeof KLUIS_TOEGANG_ROLLEN)[number]

export const KLUIS_ROL_LABEL: Record<string, string> = {
  MONTEUR: 'Monteur',
  TEAMLEIDER: 'Teamleider',
  PLANNER: 'Planner',
  HR: 'HR',
  SALARIS: 'Salaris',
  MANAGER: 'Manager',
  ADMIN: 'Admin',
}

/** Waarom een toegangsregel niet meer geldt. */
export const KLUIS_TOEGANG_VERVAL_REDENEN = [
  'VERLOPEN',
  'INGETROKKEN',
  'NIVEAU_GEWIJZIGD',
  'UIT_DIENST',
] as const
export type KluisToegangVervalReden = (typeof KLUIS_TOEGANG_VERVAL_REDENEN)[number]

// ─── 4-ogen-vrijgave ────────────────────────────────────────────────────────

export const KLUIS_VRIJGAVE_STATUSSEN = [
  'OPEN',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'INGETROKKEN',
  'VERLOPEN',
] as const
export type KluisVrijgaveStatus = (typeof KLUIS_VRIJGAVE_STATUSSEN)[number]

export const KLUIS_VRIJGAVE_STATUS_LABEL: Record<KluisVrijgaveStatus, string> = {
  OPEN: 'Wacht op beoordeling',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  INGETROKKEN: 'Ingetrokken',
  VERLOPEN: 'Verlopen',
}

/** Na goedkeuring heeft de aanvrager één uur toegang (spec §2.B.5). */
export const KLUIS_VRIJGAVE_TOEGANG_MINUTEN = 60
/** Een aanvraag waar niemand op beslist vervalt na dit aantal uren. */
export const KLUIS_VRIJGAVE_AANVRAAG_GELDIG_UREN = 72

// ─── Herbevestiging (stap-up) ───────────────────────────────────────────────

/**
 * `OutgoingEmail.triggerEntityType` van de mail met de bevestigingscode. De
 * mail-uitbox herkent hem hieraan en toont de inhoud van zo'n mail nooit.
 */
export const KLUIS_HERBEVESTIGING_MAIL_ENTITY = 'KluisHerbevestiging'

/** Wat de mail-uitbox toont in plaats van de inhoud van een codemail. */
export const KLUIS_HERBEVESTIGING_MAIL_AFGESCHERMD =
  'De inhoud van deze mail is afgeschermd: hij bevat een eenmalige bevestigingscode van de kluis.'

/**
 * Wat er in de opgeslagen mail komt te staan zodra de code niets meer waard is
 * (gebruikt, vervangen, geblokkeerd of verlopen). De code blijft zo niet in de
 * database, in back-ups of in exports achter.
 */
export const KLUIS_HERBEVESTIGING_MAIL_GEWIST =
  'Deze mail bevatte een eenmalige bevestigingscode van de STREEP-kluis. De code is gebruikt, vervangen of verlopen en is daarom uit het bericht verwijderd. Vraag in de kluis een nieuwe code aan als je er nog een nodig hebt.'

/** Het enige kanaal dat nu bestaat; Entra-re-auth is een latere uitbreiding. */
export const KLUIS_HERBEVESTIGING_KANALEN = ['EMAIL_CODE'] as const
export type KluisHerbevestigingKanaal = (typeof KLUIS_HERBEVESTIGING_KANALEN)[number]

export const KLUIS_HERBEVESTIGING_CODE_LENGTE = 6
/**
 * Onjuiste codes per gebruiker per 24 uur, over alle geheimen samen. Daarna is
 * de herbevestiging voor die gebruiker dicht tot het venster voorbij is: raden
 * blijft zo beperkt tot een handvol pogingen per dag.
 */
export const KLUIS_HERBEVESTIGING_MAX_MISLUKT_PER_DAG = 15
/** Geldigheid van een verstuurde code. */
export const KLUIS_HERBEVESTIGING_CODE_MINUTEN = 10
/** Na een juiste code geldt de herbevestiging dit aantal minuten voor dit geheim. */
export const KLUIS_HERBEVESTIGING_VENSTER_MINUTEN = 5
/** Foute pogingen per code; daarna is een nieuwe code nodig. */
export const KLUIS_HERBEVESTIGING_MAX_POGINGEN = 5
/** Aangevraagde codes per gebruiker per kwartier. */
export const KLUIS_HERBEVESTIGING_MAX_AANVRAGEN_PER_KWARTIER = 5

// ─── Weigerredenen ──────────────────────────────────────────────────────────

/**
 * Waarom `magKluisActie` weigert. Machine-leesbaar; de zichtbare tekst staat in
 * `KLUIS_WEIGER_UITLEG`. Geen enkele reden noemt het geheim of zijn waarde.
 */
export const KLUIS_WEIGER_REDENEN = [
  'ACCOUNTANT',
  'GEEN_MODULERECHT',
  'GEEN_SCHRIJFRECHT',
  'GEEN_VERWIJDERRECHT',
  'CATEGORIE',
  'NIET_OP_LIJST',
  'RECHT_ONTBREEKT',
  'ALLEEN_EIGENAAR_OF_BEHEERDER',
  'VRIJGAVE_VEREIST',
  'PORTAL_STRIKT',
  'HERBEVESTIGING_VEREIST',
] as const
export type KluisWeigerReden = (typeof KLUIS_WEIGER_REDENEN)[number]

export const KLUIS_WEIGER_UITLEG: Record<KluisWeigerReden, string> = {
  ACCOUNTANT: 'De kluis is niet beschikbaar voor dit account.',
  GEEN_MODULERECHT: 'Je hebt geen toegang tot de kluis.',
  GEEN_SCHRIJFRECHT: 'Je hebt geen schrijfrecht op de kluis.',
  GEEN_VERWIJDERRECHT: 'Je hebt geen recht om geheimen te verwijderen.',
  CATEGORIE: 'Je rol heeft geen toegang tot deze categorie.',
  NIET_OP_LIJST: 'Je staat niet op de toegangslijst van dit geheim.',
  RECHT_ONTBREEKT: 'Je toegang tot dit geheim dekt deze handeling niet.',
  ALLEEN_EIGENAAR_OF_BEHEERDER: 'Alleen de eigenaar of een beheerder mag dit.',
  VRIJGAVE_VEREIST:
    'Dit geheim is strikt beveiligd. Vraag een 4-ogen-vrijgave aan bij de eigenaar of een tweede beheerder.',
  PORTAL_STRIKT:
    'Een strikt beveiligd geheim is in de app alleen beschikbaar als je er met naam voor bent aangewezen.',
  HERBEVESTIGING_VEREIST:
    'Dit geheim is strikt beveiligd: deze handeling vraagt een bevestigingscode. Doe het per geheim, via Toegang beheren.',
}

// ─── Gedeelde invoer-schema's ───────────────────────────────────────────────

/** Bestaat deze kalenderdag echt? `2026-13-45` heeft de juiste vorm maar is geen datum. */
export function isBestaandeDag(ymd: string): boolean {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!treffer) return false
  const jaar = Number(treffer[1])
  const maand = Number(treffer[2])
  const dag = Number(treffer[3])
  const d = new Date(Date.UTC(jaar, maand - 1, dag))
  return d.getUTCFullYear() === jaar && d.getUTCMonth() === maand - 1 && d.getUTCDate() === dag
}

const ymdSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum in de vorm jjjj-mm-dd')
  .refine(isBestaandeDag, 'Deze datum bestaat niet')

const redenSchema = z
  .string()
  .trim()
  .min(KLUIS_REDEN_MIN_LENGTE, `Geef een reden van minimaal ${KLUIS_REDEN_MIN_LENGTE} tekens`)
  .max(KLUIS_REDEN_MAX_LENGTE, `Maximaal ${KLUIS_REDEN_MAX_LENGTE} tekens`)

/** Eén regel op de toegangslijst zoals het formulier hem instuurt. */
export const kluisToegangRegelSchema = z
  .object({
    subjectType: z.enum(KLUIS_TOEGANG_SUBJECT_TYPES),
    subjectRol: z.enum(KLUIS_TOEGANG_ROLLEN).nullish(),
    subjectEmployeeId: z.string().min(1).nullish(),
    subjectDepartmentId: z.string().min(1).nullish(),
    rechten: z
      .array(z.enum(KLUIS_TOEGANG_RECHTEN))
      .min(1, 'Kies minimaal één recht')
      .max(KLUIS_TOEGANG_RECHTEN.length),
    /** Laatste dag waarop de toegang geldt (Europe/Amsterdam); leeg = onbeperkt. */
    geldigTotEnMet: ymdSchema.nullish(),
    reden: z.string().trim().max(KLUIS_REDEN_MAX_LENGTE).nullish(),
  })
  .superRefine((regel, ctx) => {
    const gevuld = {
      ROL: regel.subjectRol,
      MEDEWERKER: regel.subjectEmployeeId,
      AFDELING: regel.subjectDepartmentId,
    } as const
    const veld = {
      ROL: 'subjectRol',
      MEDEWERKER: 'subjectEmployeeId',
      AFDELING: 'subjectDepartmentId',
    } as const
    if (!gevuld[regel.subjectType]) {
      ctx.addIssue({
        code: 'custom',
        path: [veld[regel.subjectType]],
        message: `Kies een ${KLUIS_TOEGANG_SUBJECT_LABEL[regel.subjectType].toLowerCase()}`,
      })
    }
    for (const type of KLUIS_TOEGANG_SUBJECT_TYPES) {
      if (type !== regel.subjectType && gevuld[type]) {
        ctx.addIssue({
          code: 'custom',
          path: [veld[type]],
          message: 'Een regel heeft precies één subject',
        })
      }
    }
  })
export type KluisToegangRegelInput = z.infer<typeof kluisToegangRegelSchema>

/** Stabiele sleutel van het subject van een regel (dubbelen-check, diff). */
export function kluisToegangSubjectSleutel(regel: {
  subjectType: KluisToegangSubjectType
  subjectRol?: string | null
  subjectEmployeeId?: string | null
  subjectDepartmentId?: string | null
}): string {
  switch (regel.subjectType) {
    case 'ROL':
      return `ROL:${regel.subjectRol ?? ''}`
    case 'MEDEWERKER':
      return `MEDEWERKER:${regel.subjectEmployeeId ?? ''}`
    case 'AFDELING':
      return `AFDELING:${regel.subjectDepartmentId ?? ''}`
  }
}

/**
 * Niveau, eigenaar en toegangslijst van één geheim, als geheel. De lijst is een
 * VERVANG-set: wat er niet in staat, vervalt (met audit).
 */
export const kluisToegangOpslaanSchema = z
  .object({
    niveau: z.enum(KLUIS_NIVEAUS),
    eigenaarEmployeeId: z.string().min(1, 'Kies een eigenaar'),
    toegang: z.array(kluisToegangRegelSchema).max(100),
    reden: redenSchema,
  })
  .superRefine((data, ctx) => {
    const gezien = new Set<string>()
    data.toegang.forEach((regel, index) => {
      if (kluisNiveauAlleenPersonen(data.niveau) && regel.subjectType !== 'MEDEWERKER') {
        ctx.addIssue({
          code: 'custom',
          path: ['toegang', index, 'subjectType'],
          message: 'Toegang geef je per persoon, niet per rol of afdeling',
        })
      }
      const sleutel = kluisToegangSubjectSleutel(regel)
      if (gezien.has(sleutel)) {
        ctx.addIssue({
          code: 'custom',
          path: ['toegang', index, 'subjectType'],
          message: 'Dit subject staat al op de lijst',
        })
      }
      gezien.add(sleutel)
    })
  })
export type KluisToegangOpslaanInput = z.infer<typeof kluisToegangOpslaanSchema>

/** Body van onthullen/kopiëren. Welke velden verplicht zijn volgt uit het niveau. */
export const kluisOnthulSchema = z.object({
  reden: z.string().trim().max(KLUIS_REDEN_MAX_LENGTE).optional(),
  code: z
    .string()
    .trim()
    .regex(
      new RegExp(`^\\d{${KLUIS_HERBEVESTIGING_CODE_LENGTE}}$`),
      `De code bestaat uit ${KLUIS_HERBEVESTIGING_CODE_LENGTE} cijfers`,
    )
    .optional(),
})
export type KluisOnthulInput = z.infer<typeof kluisOnthulSchema>

export const kluisVrijgaveAanvraagSchema = z.object({ reden: redenSchema })
export type KluisVrijgaveAanvraagInput = z.infer<typeof kluisVrijgaveAanvraagSchema>

export const kluisVrijgaveAfwijzenSchema = z.object({ toelichting: redenSchema })
export const kluisVrijgaveGoedkeurenSchema = z.object({
  toelichting: z.string().trim().max(KLUIS_REDEN_MAX_LENGTE).optional(),
})

export const kluisEigenaarOverdrachtSchema = z
  .object({
    vanEmployeeId: z.string().min(1, 'Kies de huidige eigenaar'),
    naarEmployeeId: z.string().min(1, 'Kies de nieuwe eigenaar'),
    /** Leeg = alle geheimen van de huidige eigenaar. */
    itemIds: z.array(z.string().min(1)).max(500).optional(),
    reden: redenSchema,
  })
  .refine((d) => d.vanEmployeeId !== d.naarEmployeeId, {
    path: ['naarEmployeeId'],
    message: 'Kies een andere medewerker dan de huidige eigenaar',
  })
export type KluisEigenaarOverdrachtInput = z.infer<typeof kluisEigenaarOverdrachtSchema>

/** Is het standaardniveau van een categorie minstens zijn eigen minimum? */
export function kluisCategorieNiveausKloppen(d: {
  standaardNiveau: KluisNiveau
  minimumNiveau: KluisNiveau
}): boolean {
  return kluisNiveauVoldoet(d.standaardNiveau, d.minimumNiveau)
}

// ─── DTO's ──────────────────────────────────────────────────────────────────

/** Via welke weg iemand bij een geheim komt. */
export const KLUIS_TOEGANG_WEGEN = [
  'EIGENAAR',
  'BEHEERDER',
  'CATEGORIE',
  'LIJST_PERSOON',
  'LIJST_ROL',
  'LIJST_AFDELING',
  'VRIJGAVE',
] as const
export type KluisToegangWeg = (typeof KLUIS_TOEGANG_WEGEN)[number]

export const KLUIS_TOEGANG_WEG_LABEL: Record<KluisToegangWeg, string> = {
  EIGENAAR: 'Eigenaar',
  BEHEERDER: 'Beheerder',
  CATEGORIE: 'Via de categorie',
  LIJST_PERSOON: 'Met naam op de lijst',
  LIJST_ROL: 'Via rol op de lijst',
  LIJST_AFDELING: 'Via afdeling op de lijst',
  VRIJGAVE: '4-ogen-vrijgave',
}

/** Wat de ingelogde gebruiker met dit geheim mag (server-berekend, voor de UI). */
export interface KluisItemMagDTO {
  onthullen: boolean
  /** Reden waarom onthullen niet mag; `VRIJGAVE_VEREIST` opent het 4-ogen-pad. */
  onthullenWeigerReden: KluisWeigerReden | null
  bewerken: boolean
  beheren: boolean
  verwijderen: boolean
  auditInzien: boolean
  vereisten: KluisOnthulVereisten
}

export interface KluisToegangRegelDTO {
  id: string
  subjectType: KluisToegangSubjectType
  subjectRol: string | null
  subjectEmployeeId: string | null
  subjectDepartmentId: string | null
  /** Weergavenaam van het subject (rol-label, naam medewerker, afdelingsnaam). */
  subjectNaam: string
  rechten: KluisToegangRecht[]
  /** Laatste geldige dag (jjjj-mm-dd, Europe/Amsterdam) of null = onbeperkt. */
  geldigTotEnMet: string | null
  reden: string | null
  verleendDoorNaam: string | null
  verleendOp: string
  /** Subject valt buiten de categorie-rollen: de regel geeft geen toegang. */
  buitenCategorie: boolean
}

/** Eén persoon in de samenvatting "wie kan dit zien". */
export interface KluisZichtPersoonDTO {
  userId: string
  naam: string
  rol: string
  weg: KluisToegangWeg
  zien: boolean
  onthullen: boolean
  bewerken: boolean
  beheren: boolean
  /** Laatste geldige dag van tijdelijke toegang, of null. */
  geldigTotEnMet: string | null
  /**
   * Geen backoffice-toegang: deze persoon komt er alleen in de app bij, op een
   * werkbon waar het geheim aan hangt. Bewerken en beheren kan dan niet.
   */
  alleenPortal: boolean
}

export interface KluisZichtSamenvattingDTO {
  niveau: KluisNiveau
  personen: KluisZichtPersoonDTO[]
  /** Aantal mensen dat de waarde kan onthullen. */
  aantalOnthullen: number
  /** Meldingen die de beheerder moet zien vóór opslaan (nooit stil). */
  waarschuwingen: string[]
}

export interface KluisToegangDetailDTO {
  itemId: string
  niveau: KluisNiveau
  categorieMinimumNiveau: KluisNiveau
  eigenaarEmployeeId: string | null
  eigenaarNaam: string | null
  /** De eigenaar is uit dienst of heeft geen account waarmee hij het geheim kan beheren. */
  eigenaarInactief: boolean
  toegang: KluisToegangRegelDTO[]
  /**
   * "Wie kan dit zien". Beheer-informatie: alleen gevuld voor de eigenaar, een
   * beheerder of wie een vrijgave heeft; null voor wie het geheim alleen via de
   * categorie ziet.
   */
  samenvatting: KluisZichtSamenvattingDTO | null
}

export interface KluisVrijgaveAanvraagDTO {
  id: string
  kluisItemId: string
  /** Naam van het geheim; null als de kijker het bestaan niet mag zien. */
  itemNaam: string | null
  aanvragerId: string
  aanvragerNaam: string | null
  reden: string
  status: KluisVrijgaveStatus
  aangevraagdOp: string
  /** Tot wanneer een openstaande aanvraag beoordeeld kan worden. */
  vervaltOp: string
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  beoordelingToelichting: string | null
  /** Einde van het toegangsvenster na goedkeuring. */
  toegangTot: string | null
  /** Mag de ingelogde gebruiker deze aanvraag beoordelen? */
  magBeoordelen: boolean
  /** Is de ingelogde gebruiker de aanvrager? */
  eigenAanvraag: boolean
  /**
   * Mag de ingelogde gebruiker intrekken? De aanvrager een open aanvraag of zijn
   * lopende toegang; de eigenaar een lopende toegang.
   */
  magIntrekken: boolean
}

/** Eén regel in het overzicht "wie heeft toegang tot wat" per medewerker. */
export interface KluisMedewerkerToegangRegelDTO {
  itemId: string
  naam: string
  type: string
  niveau: KluisNiveau
  categorieNaam: string | null
  weg: KluisToegangWeg
  zien: boolean
  onthullen: boolean
  bewerken: boolean
  beheren: boolean
  eigenaar: boolean
  geldigTotEnMet: string | null
}

export interface KluisMedewerkerToegangDTO {
  employeeId: string
  medewerkerNaam: string
  /** De medewerker heeft geen actief account met kluisrecht: de lijst is dan leeg. */
  heeftKluisrecht: boolean
  regels: KluisMedewerkerToegangRegelDTO[]
  /**
   * Geheimen waar de medewerker bij kan maar die de kijker zelf niet mag zien.
   * Alleen het aantal: de naam van zo'n geheim hoort niet in dit overzicht.
   */
  afgeschermd: number
  /** Aantal geheimen waarvan de medewerker eigenaar is (ook de afgeschermde). */
  aantalEigenaar: number
}

export interface KluisEigenaarOverdrachtResultaat {
  overgedragen: number
  /** Geheimen die de aanroeper wel mag zien maar niet mag overdragen, met de reden. */
  geweigerd: Array<{ itemId: string; naam: string; reden: KluisWeigerReden }>
  /**
   * Aantal geheimen van deze eigenaar dat de aanroeper niet mag zien en dus ook
   * niet kon overdragen. Alleen een telling: nooit een id, naam of niveau.
   */
  afgeschermd: number
}

export interface KluisHerbevestigingAanvraagDTO {
  kanaal: KluisHerbevestigingKanaal
  /** Gemaskeerd adres waar de code heen is (bv. "j***@streep.nl"). */
  adresHint: string
  verlooptOp: string
}

/** Labels van de audit-acties (bestaand plus FO-495). */
export const KLUIS_AUDIT_ACTIE_LABEL: Record<string, string> = {
  AANGEMAAKT: 'Aangemaakt',
  BIJGEWERKT: 'Bijgewerkt',
  VERWIJDERD: 'Verwijderd',
  ONTHULD: 'Onthuld',
  GEKOPIEERD: 'Gekopieerd',
  TOEGANG_GEWIJZIGD: 'Toegang gewijzigd',
  CATEGORIE_GEWIJZIGD: 'Categorie gewijzigd',
  NIVEAU_GEWIJZIGD: 'Niveau gewijzigd',
  EIGENAAR_GEWIJZIGD: 'Eigenaar gewijzigd',
  TOEGANG_VERLEEND: 'Toegang verleend',
  TOEGANG_INGETROKKEN: 'Toegang ingetrokken',
  TOEGANG_VERLOPEN: 'Tijdelijke toegang verlopen',
  TOEGANG_VERVALLEN: 'Toegang vervallen',
  ONTHULLEN_GEWEIGERD: 'Onthullen geweigerd',
  HERBEVESTIGING_VERSTUURD: 'Bevestigingscode verstuurd',
  HERBEVESTIGING_MISLUKT: 'Bevestigingscode onjuist',
  VRIJGAVE_AANGEVRAAGD: '4-ogen-vrijgave aangevraagd',
  VRIJGAVE_GOEDGEKEURD: '4-ogen-vrijgave goedgekeurd',
  VRIJGAVE_AFGEWEZEN: '4-ogen-vrijgave afgewezen',
  VRIJGAVE_INGETROKKEN: '4-ogen-vrijgave ingetrokken',
  VRIJGAVE_VERLOPEN: '4-ogen-vrijgave verlopen',
}

/** Actor-waarde voor audit-regels die het systeem zelf schrijft (cron, migratie). */
export const KLUIS_AUDIT_SYSTEEM_ACTOR = 'systeem'

/** Maskeer een e-mailadres voor weergave: eerste teken plus domein. */
export function maskeerEmailadres(email: string): string {
  const at = email.lastIndexOf('@')
  if (at <= 0) return '***'
  return `${email[0]}***${email.slice(at)}`
}
